import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import OutlinedInput from '@mui/material/OutlinedInput';
import Popover from '@mui/material/Popover';
import React, { useEffect, useState } from 'react';
import { SearchableMultiSelect } from 'shared/components/SearchableMultiSelect';

import {
  ApplyButton,
  ClearFiltersButton,
  FilterActionsContainer,
  FilterArrowIcon,
  FilterDescriptionPaper,
  FilterDropdownButton,
  FilterDropdownPaper,
  FilterMiniCheckbox,
  FilterOptionRow,
  FilterSectionLabel,
  FiltersRow,
  FiltersSection,
  ClearFiltersContainer,
  filterDescriptionInputSx,
} from '../reports.styles';
import type { ReportFiltersState } from '../types/reports.types';
import { hasActiveFilters } from '../utils/reportsUtils';

export interface ReportFiltersBarProps {
  filters: ReportFiltersState;
  onFiltersChange: (filters: ReportFiltersState) => void;
  onApply: (filters: ReportFiltersState) => void;
  onClearFilters: () => void;
  isLoading?: boolean;
  clients: Array<{ id: string; name: string; isArchived?: boolean }>;
  projects: Array<{ id: string; name: string; isArchived?: boolean }>;
  team: Array<{ id: string; name: string; status?: string }>;
}

export const ReportFiltersBar: React.FC<ReportFiltersBarProps> = ({
  filters,
  onFiltersChange,
  onApply,
  onClearFilters,
  isLoading = false,
  clients = [],
  projects = [],
  team = [],
}) => {
  const [localFilters, setLocalFilters] = useState(filters);
  const [statusAnchor, setStatusAnchor] = useState<HTMLButtonElement | null>(null);
  const [descAnchor, setDescAnchor] = useState<HTMLButtonElement | null>(null);
  const [applySignal, setApplySignal] = useState(0);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleFilterChange = (updates: Partial<ReportFiltersState>) => {
    setLocalFilters((prev) => ({ ...prev, ...updates }));
  };

  const handleClearFilters = () => {
    setLocalFilters({});
    onFiltersChange({});
    onClearFilters();
  };

  const handleApply = () => {
    onFiltersChange(localFilters);
    onApply(localFilters);
    setApplySignal((s) => s + 1);
  };

  // ── Status (billable) ──────────────────────────────────────────────────────
  const isBillableChecked = localFilters.billable === true;
  const isNonBillableChecked = localFilters.billable === false;
  const statusActiveCount = (isBillableChecked ? 1 : 0) + (isNonBillableChecked ? 1 : 0);

  const toggleBillable = (value: boolean) => {
    const next = localFilters.billable === value ? null : value;
    handleFilterChange({ billable: next });
  };

  // ── Description ───────────────────────────────────────────────────────────
  const hasSearch = !!localFilters.descriptionSearch;
  const hasWithoutDesc = localFilters.hasDescription === false;
  const descActiveCount = (hasSearch ? 1 : 0) + (hasWithoutDesc ? 1 : 0);

  return (
    <FiltersRow>
      <FiltersSection>
        {/* Team Filter */}
        <SearchableMultiSelect
          pill
          label="Team"
          options={team}
          value={localFilters.userIds || []}
          onChange={(selected) =>
            handleFilterChange({ userIds: selected.length > 0 ? selected : undefined })
          }
          statusField="status"
          applySignal={applySignal}
        />

        {/* Client Filter */}
        <SearchableMultiSelect
          pill
          label="Client"
          options={clients}
          value={localFilters.clientIds || []}
          onChange={(selected) =>
            handleFilterChange({ clientIds: selected.length > 0 ? selected : undefined })
          }
          statusField="isArchived"
          applySignal={applySignal}
        />

        {/* Projects Filter */}
        <SearchableMultiSelect
          pill
          label="Projects"
          options={projects}
          value={localFilters.projectIds || []}
          onChange={(selected) =>
            handleFilterChange({ projectIds: selected.length > 0 ? selected : undefined })
          }
          statusField="isArchived"
          applySignal={applySignal}
        />

        {/* Status Filter — checkbox dropdown */}
        <FilterDropdownButton onClick={(e) => setStatusAnchor(e.currentTarget)}>
          <span>{statusActiveCount > 0 ? `Status (${statusActiveCount})` : 'Status'}</span>
          <FilterArrowIcon />
        </FilterDropdownButton>
        <Popover
          open={Boolean(statusAnchor)}
          anchorEl={statusAnchor}
          onClose={() => setStatusAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{
            paper: { elevation: 4, sx: { overflow: 'hidden', mt: 0.5, borderRadius: 1.5 } },
          }}
        >
          <FilterDropdownPaper>
            <FilterSectionLabel>Billable</FilterSectionLabel>
            <FilterOptionRow isSelected={isBillableChecked} onClick={() => toggleBillable(true)}>
              <FilterMiniCheckbox isSelected={isBillableChecked}>
                {isBillableChecked ? '✓' : ''}
              </FilterMiniCheckbox>
              Billable
            </FilterOptionRow>
            <FilterOptionRow
              isSelected={isNonBillableChecked}
              onClick={() => toggleBillable(false)}
            >
              <FilterMiniCheckbox isSelected={isNonBillableChecked}>
                {isNonBillableChecked ? '✓' : ''}
              </FilterMiniCheckbox>
              Non-billable
            </FilterOptionRow>
          </FilterDropdownPaper>
        </Popover>

        {/* Description Filter — search input + without description checkbox */}
        <FilterDropdownButton onClick={(e) => setDescAnchor(e.currentTarget)}>
          <span>{descActiveCount > 0 ? `Description (${descActiveCount})` : 'Description'}</span>
          <FilterArrowIcon />
        </FilterDropdownButton>
        <Popover
          open={Boolean(descAnchor)}
          anchorEl={descAnchor}
          onClose={() => setDescAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{
            paper: { elevation: 4, sx: { overflow: 'hidden', mt: 0.5, borderRadius: 1.5 } },
          }}
        >
          <FilterDescriptionPaper>
            <OutlinedInput
              size="small"
              fullWidth
              placeholder="Enter description…"
              value={localFilters.descriptionSearch ?? ''}
              onChange={(e) =>
                handleFilterChange({ descriptionSearch: e.target.value || undefined })
              }
              startAdornment={
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              }
              sx={filterDescriptionInputSx}
            />
            <FilterOptionRow
              isSelected={hasWithoutDesc}
              onClick={() =>
                handleFilterChange({ hasDescription: hasWithoutDesc ? undefined : false })
              }
            >
              <FilterMiniCheckbox isSelected={hasWithoutDesc}>
                {hasWithoutDesc ? '✓' : ''}
              </FilterMiniCheckbox>
              Without description
            </FilterOptionRow>
          </FilterDescriptionPaper>
        </Popover>

        {/* Apply and Clear Actions */}
        <FilterActionsContainer>
          {hasActiveFilters(filters) && (
            <ClearFiltersContainer>
              <ClearFiltersButton type="button" onClick={handleClearFilters}>
                Clear filters
              </ClearFiltersButton>
            </ClearFiltersContainer>
          )}
          <ApplyButton
            variant="contained"
            color="primary"
            size="small"
            disableElevation
            onClick={handleApply}
            disabled={isLoading}
          >
            Apply
          </ApplyButton>
        </FilterActionsContainer>
      </FiltersSection>
    </FiltersRow>
  );
};
